import { useState, useEffect, useCallback } from 'react';

const VAPID_PUBLIC_KEY = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ?? '';

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export type PushState = 'unsupported' | 'blocked' | 'unsubscribed' | 'subscribed' | 'loading';

/**
 * @param token - The user's OneLogin access token. Pass '' to disable.
 */
export function usePushNotifications(token: string) {
  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    !!VAPID_PUBLIC_KEY;

  const [state, setState] = useState<PushState>(supported ? 'loading' : 'unsupported');

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (Notification.permission === 'denied') {
          setState('blocked');
        } else if (sub) {
          setState('subscribed');
        } else {
          setState('unsubscribed');
        }
      })
      .catch(() => setState('unsubscribed'));
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported || !token) return;
    setState('loading');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      const r = await fetch('/api/push-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription: sub.toJSON() }),
      });
      setState(r.ok ? 'subscribed' : 'unsubscribed');
    } catch {
      setState(Notification.permission === 'denied' ? 'blocked' : 'unsubscribed');
    }
  }, [supported, token]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setState('loading');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
      if (token) {
        await fetch('/api/push-unsubscribe', {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setState('unsubscribed');
    } catch {
      setState('subscribed');
    }
  }, [supported, token]);

  return { state, subscribe, unsubscribe };
}
